// client/src/components/MerchantFidGestion.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { firestore, storage } from '../../firebase';
import { doc, collection, query, where, getDocs, setDoc, getDoc, onSnapshot, limit as qLimit } from 'firebase/firestore';
import { getAuth, onAuthStateChanged, sendPasswordResetEmail } from 'firebase/auth';
import { ref, getDownloadURL, listAll, getMetadata } from 'firebase/storage';
import '../CSS/Dashboard.css';
import { useNavigate } from 'react-router-dom';
import MerchantNavbar from './MerchantNavbar';
import { QRCodeCanvas } from 'qrcode.react';
import { FaDownload, FaArrowLeft, FaLock, FaStar, FaCheckCircle, FaTrash } from 'react-icons/fa';
import api from '../../api';
import { canonicalizeEmail } from '../../utils/emailCanonicalize';
import {
  upsertLinkedAccountWithPassword,
  saveRemoteLinkedAccounts,
  deleteLinkedAccount,
  removeRemoteLinkedAccountEntry,
} from '../../utils/accountLinkStorage';

// >>> Politique MDP centralisée
import { REGEX as PASSWORD_REGEX, POLICY as PASSWORD_POLICY } from '../../policies/passwordPolicy';
import PasswordField from '../shared/PasswordField';

// ── util: tronquer pour mobile ──
function truncateForMobile(str = '', limit = 15) {
  const s = String(str || '');
  if (s.length <= limit) return s;
  if (limit <= 3) return '...';
  return s.slice(0, limit - 3) + '...';
}

// Cadre du QR servi par l'app (même origine) : pas de dépendance au CORS du bucket Storage,
// indispensable pour dessiner le cadre dans le canvas du téléchargement.
const QR_FRAME_SRC = '/qr-frame.png';

// ── QRCodeDisplay (défini hors de MerchantSettings pour éviter les remounts) ──
const QRCodeDisplay = React.memo(function QRCodeDisplay({ value, caption }) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const bgSrcRef = useRef(
    QR_FRAME_SRC
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const internalSize = isMobile ? 500 : 1750;
  const displaySize = isMobile ? 80 : 110;
  const containerWidth = isMobile ? '150px' : '200px';

  return (
    <div id="qr-container" style={{ position: 'relative', display: 'inline-block', width: containerWidth }}>
      <img
        crossOrigin="anonymous"
        src={bgSrcRef.current}
        alt="Fond QR Code"
        style={{ display: 'block', width: '100%', height: 'auto' }}
        draggable={false}
      />
      {/* Bloc absolu centré : QR + légende juste dessous */}
      <div style={{ position: 'absolute', top: '55%', left: '50%', transform: 'translate(-50%, -50%)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <QRCodeCanvas
            id="qr-code-canvas"
            value={value}
            size={internalSize}
            level="H"
            includeMargin={false}
            bgColor="transparent"
            style={{ width: `${displaySize}px`, height: `${displaySize}px` }}
          />
          {caption ? (
            <div
              style={{
                marginTop: isMobile ? 4 : 6,
                fontSize: isMobile ? '9px' : '12px',
                lineHeight: 1.1,
                color: '#111',
                fontFamily: 'monospace',
                whiteSpace: 'nowrap',
                textAlign: 'center'
              }}
            >
              {caption}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}, (prev, next) => prev.value === next.value && prev.caption === next.caption);

const MerchantFidGestion = () => {
  const [user, setUser] = useState(null);
  const [userPseudo, setUserPseudo] = useState('');
  // eslint-disable-next-line
  const [photos, setPhotos] = useState([]);
  const navigate = useNavigate();
  // eslint-disable-next-line
  const [invitations, setInvitations] = useState([]);
  // eslint-disable-next-line
  const [showModal, setShowModal] = useState(false);
  // eslint-disable-next-line
  const [fidelityCreated, setFidelityCreated] = useState(false);
  const [multiple, setMultiple] = useState(5);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [gift, setGift] = useState("5%");
  const [fidelityStatus, setFidelityStatus] = useState("");
  const [initialFidelity, setInitialFidelity] = useState(null);
  const [multiScan, setMultiScan] = useState(false);
  const [newCaisseLoading, setNewCaisseLoading] = useState(false);

  // Sélecteur de type fidélité
  const [fidelityType, setFidelityType] = useState("Par passage");

  // Fidélité par points
  const [levels, setLevels] = useState([{ points: 50, gift: "" }]);

  // Modale "Nouvelle caisse"
  const [showNewCaisseModal, setShowNewCaisseModal] = useState(false);
  const [newCaisseEmail, setNewCaisseEmail] = useState('');
  const [newCaisseMessage, setNewCaisseMessage] = useState(null);
  const [newCaisseError, setNewCaisseError] = useState(null);

  // >>> Mot de passe auto / manuel pour la création de caisse
  const [useAutoPassword, setUseAutoPassword] = useState(true);
  const [autoPassword, setAutoPassword] = useState('');
  const [manualPassword, setManualPassword] = useState('');
  // eslint-disable-next-line
  const [manualPwdTouched, setManualPwdTouched] = useState(false);
  // eslint-disable-next-line
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [cashiers, setCashiers] = useState([]);
  const [shortId, setShortId] = useState('');
  const [showAllCashiers, setShowAllCashiers] = useState(false);

  // Satisfaction
  const [satisfactionMode, setSatisfactionMode] = useState('ecobill');
  const [googleReviewUrl, setGoogleReviewUrl] = useState('');
  const [facebookReviewUrl, setFacebookReviewUrl] = useState('');
  const [savingSatisfaction, setSavingSatisfaction] = useState(false);
  const [satisfactionSaved, setSatisfactionSaved] = useState(false);

  // XS breakpoint pour icône seule sur le bouton reset (<400px)
  const [isXS, setIsXS] = useState(() => window.innerWidth <= 400);
  // Phone breakpoint (≤480px) pour l’affichage email & “gains”
  const [isPhone, setIsPhone] = useState(() => window.innerWidth <= 480);

  useEffect(() => {
    const onResize = () => {
      setIsXS(window.innerWidth <= 400);
      setIsPhone(window.innerWidth <= 480);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isFidelityUnchanged =
    initialFidelity &&
    fidelityType === initialFidelity.type &&
    ((fidelityType === "Par passage" && Number(multiple) === Number(initialFidelity.multiple) && gift === initialFidelity.gift) ||
      (fidelityType === "Par points" &&
        JSON.stringify(levels.map(l => ({ ...l, points: Number(l.points) }))) ===
        JSON.stringify(initialFidelity.levels.map(l => ({ ...l, points: Number(l.points) }))))) &&
    multiScan === initialFidelity.multiscan &&
    userPseudo === user?.pseudo;

  const isFidelityInvalid = fidelityType === "Par passage"
    ? (!multiple || Number(multiple) === 0 || !gift)
    : levels.some(level => !level.points || Number(level.points) === 0 || !level.gift || level.error);

  const isUpdateDisabled = isFidelityUnchanged || isFidelityInvalid;

  useEffect(() => {
    const storedInvitations = localStorage.getItem("invitations");
    if (storedInvitations) setInvitations(JSON.parse(storedInvitations));
  }, []);

  const fetchInvitations = useCallback(async (email) => {
    const entRef = collection(firestore, 'entreprises');
    // Firestore rules: list queries on "entreprises" must include a limit <= 50
    const qy = query(entRef, where('employeeEmail','==',email), where('status','==','pending'), qLimit(50));
    const snap = await getDocs(qy);
    const f = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setInvitations(f);
    localStorage.setItem("invitations", JSON.stringify(f));
  }, []);

  const getEmployeeUidByEmail = useCallback(async (email) => {
    // Sélectionne uniquement les caisses de MA propre entreprise pour rester dans le périmètre des règles
    // Firestore (lecture user permise si role == cashier && companyuid == request.auth.uid)
    try {
      const auth = getAuth();
      const currentUid = auth.currentUser?.uid || null;
      const usersRef = collection(firestore, 'users');

      const constraints = [where('email', '==', email), where('role', '==', 'cashier')];
      if (currentUid) {
        constraints.push(where('companyuid', '==', currentUid));
      }

      const qy = query(usersRef, ...constraints);
      const snap = await getDocs(qy);
      return snap.empty ? null : snap.docs[0].id;
    } catch (err) {
      // Ne bloque pas la récupération des photos si un user n'est pas lisible (permission‑denied)
      if (err?.code === 'permission-denied') {
        console.warn('[MerchantFidGestion] accès refusé sur users/email lookup pour', email);
        return null;
      }
      console.error('[MerchantFidGestion] getEmployeeUidByEmail error:', err);
      return null;
    }
  }, []);

  const fetchPhotos = useCallback(async () => {
    try {
      const userId = getAuth().currentUser?.uid;
      const userEmail = getAuth().currentUser?.email;
      if (!userId || !userEmail) return;

      // Storage rules forbid listing the root user-photos/. We must scope to the merchant folder.
      const photosRef = ref(storage, `user-photos/${userId}/`);
      const photoList = await listAll(photosRef);

      const entrepriseRef = collection(firestore, 'entreprises');
      // add limit to comply with Firestore rules
      const entrepriseSnapshot = await getDocs(query(entrepriseRef, qLimit(50)));
      const acceptedEmployees = [];

      const userEntreprise = entrepriseSnapshot.docs.find(
        d => d.data().email === userEmail
      );

      if (userEntreprise) {
        const entrepriseData = userEntreprise.data();
        if (entrepriseData?.invitations) {
          for (const invitation of entrepriseData.invitations) {
            if (invitation.status === 'accepted' && invitation.employeeEmail) {
              const employeeUid = await getEmployeeUidByEmail(invitation.employeeEmail);
              if (employeeUid) acceptedEmployees.push(employeeUid);
            }
          }
        }
      }

      const photosURLs = await Promise.all(
        photoList.items
          .filter(item => {
            const isUserPhoto = item.name.includes(userId);
            const isEmployeePhoto = acceptedEmployees.some(uid => item.name.includes(uid));
            return isUserPhoto || isEmployeePhoto;
          })
          .map(async item => {
            const url = await getDownloadURL(item);
            const metadata = await getMetadata(item);
            const createdAt = metadata.timeCreated ? new Date(metadata.timeCreated) : new Date();
            const formattedDate = createdAt.toLocaleString('fr-FR', {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            });

            const employeeEmail = metadata.customMetadata?.TakenByemail || "Inconnu";
            const employeePseudo = metadata.customMetadata?.TakenByPseudo || "Inconnu";

            if (metadata.customMetadata?.category === "pro" && !item.name.includes(userId)) {
              return { name: item.name, url, date: formattedDate, employeeEmail, employeePseudo };
            }
            if (item.name.includes(userId)) {
              return { name: item.name, url, date: formattedDate, employeeEmail, employeePseudo };
            }
            return null;
          })
      );

      setPhotos(photosURLs.filter(Boolean).sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch (error) {
      console.error("Erreur lors de la récupération des photos :", error);
      alert("Impossible de récupérer les photos.");
    }
  }, [getEmployeeUidByEmail]);

  // Auth + snapshot user avec cleanup correct
  useEffect(() => {
    const auth = getAuth();
    let unsubscribeUser = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      // Nettoie l'ancien listener s'il existe
      if (unsubscribeUser) {
        unsubscribeUser();
        unsubscribeUser = null;
      }

      if (!firebaseUser) {
        navigate('/logingeneral');
        return;
      }

      const userId = firebaseUser.uid;
      const userDocRef = doc(firestore, 'users', userId);

      unsubscribeUser = onSnapshot(userDocRef, (snap) => {
        const data = snap.data();
        setUser({ ...data, uid: userId });

        if (data?.pseudo) setUserPseudo(data.pseudo);
        else setShowModal(true);

        fetchPhotos();

        if (data?.keystatus === "active") {
          (async () => {
            const entRef = collection(firestore, 'entreprises');
            const entSnap = await getDocs(query(entRef, qLimit(50)));
            const myEnt = entSnap.docs.find(d => d.data().createdBy === data.email);
            if (myEnt) {
              const accepted = (myEnt.data().invitations || []).filter(i => i.status === 'accepted');
              const withId = accepted.map(i => ({ ...i, id: myEnt.id }));
              setInvitations(withId);
              localStorage.setItem("invitations", JSON.stringify(withId));
            } else {
              setInvitations([]);
              localStorage.setItem("invitations", JSON.stringify([]));
            }
          })();
        } else if (data?.email) {
          fetchInvitations(data.email);
        }
      });
    });

    return () => {
      if (unsubscribeUser) unsubscribeUser();
      unsubscribeAuth();
    };
  }, [navigate, fetchPhotos, fetchInvitations]);

  useEffect(() => {
    if (fidelityType === "Par points" && !multiScan) setMultiScan(true);
  }, [fidelityType, multiScan]);

  useEffect(() => {
    if (!user?.uid) return;
    const fidRef = doc(firestore, "fidelity", user.uid);
    const unsub = onSnapshot(fidRef, snap => {
      if (snap.exists()) {
        const d = snap.data();
        setFidelityCreated(true);
        setFidelityStatus(d.status);

        setFidelityType(d.Type === "Fidbypoints" ? "Par points" : "Par passage");
        setMultiScan(d.multiscan === "yes");
        setMultiple(d.multiple || 5);
        setGift(d.gift || "5%");

        const loadedLevels = d.levels || [
          { points: d.points1 || 100, gift: d.gift1 || "" },
          { points: d.points2 || 250, gift: d.gift2 || "" },
          { points: d.points3 || 500, gift: d.gift3 || "" },
        ].filter(l => l.points > 0);

        const normalizedLevels =
          (loadedLevels.length > 0 ? loadedLevels : [{ points: 50, gift: "" }]).map(level => ({
            points: level?.points ?? "",
            gift: level?.gift ?? "",
            error: false,
          }));

        setLevels(normalizedLevels);
        setSatisfactionMode(d.satisfactionMode || 'ecobill');
        setGoogleReviewUrl(d.googleReviewUrl || '');
        setFacebookReviewUrl(d.facebookReviewUrl || '');

        const initialData = {
          multiscan: d.multiscan === "yes",
          type: d.Type === "Fidbypoints" ? "Par points" : "Par passage",
          multiple: d.multiple || 5,
          gift: d.gift || "5%",
          levels: normalizedLevels.map(level => ({ ...level })),
        };
        setInitialFidelity(initialData);
        setShortId(d.ShortID || '');   
      } else {
        setFidelityCreated(false);
        setFidelityStatus(null);
        setFidelityType("Par passage");
        setMultiScan(false);
        setMultiple(5);
        setGift("5%");
        const defaultLevels = [{ points: 50, gift: "", error: false }];
        setLevels(defaultLevels);

        const initialData = {
          multiscan: false,
          type: "Par passage",
          multiple: 5,
          gift: "5%",
          levels: defaultLevels.map(level => ({ ...level })),
        };
        setInitialFidelity(initialData);
        setShortId('');     
      }
    });
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user?.uid) return;
    const cQuery = query(
      collection(firestore, 'users'),
      where('companyuid', '==', user.uid),
      where('role', '==', 'cashier')
    );
    const unsubC = onSnapshot(cQuery, snap => {
      setCashiers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsubC();
  }, [user]);

  const handleSaveSatisfactionMode = async () => {
    if (!user?.uid) return;
    setSavingSatisfaction(true);
    try {
      const fidelityDocRef = doc(firestore, 'fidelity', user.uid);
      const hasGoogle = googleReviewUrl.trim().length > 0;
      const hasFacebook = facebookReviewUrl.trim().length > 0;
      const effectiveMode = (satisfactionMode === 'google+facebook' && !hasGoogle && !hasFacebook)
        ? 'ecobill'
        : satisfactionMode;
      if (effectiveMode === 'ecobill') setSatisfactionMode('ecobill');
      const payload = { satisfactionMode: effectiveMode };
      if (effectiveMode === 'google+facebook') {
        payload.googleReviewUrl = googleReviewUrl.trim();
        payload.facebookReviewUrl = facebookReviewUrl.trim();
      }
      await setDoc(fidelityDocRef, payload, { merge: true });
      setSatisfactionSaved(true);
      setTimeout(() => setSatisfactionSaved(false), 2500);
    } catch (e) {
      console.error('Erreur sauvegarde satisfaction:', e);
    } finally {
      setSavingSatisfaction(false);
    }
  };

  const handleUpdateFidelity = async () => {
    if (!user?.uid) {
      setErrorStatus("Erreur de mise à jour : utilisateur non authentifié.");
      return;
    }
    try {
      const fidelityData = {
        multiscan: multiScan ? "yes" : "no",
        Type: fidelityType === "Par points" ? "Fidbypoints" : "Fidbycount",
        pseudo: userPseudo,
      };

      if (fidelityType === "Par points") {
        for (let i = 1; i < levels.length; i++) {
          if (Number(levels[i].points) <= Number(levels[i - 1].points)) {
            setErrorStatus(`Validation échouée : Le niveau ${i + 1} doit avoir plus de points que le niveau ${i}.`);
            setTimeout(() => setErrorStatus(null), 5000);
            return;
          }
        }
        fidelityData.levels = levels.map(l => ({ ...l, points: Number(l.points) }));
      } else {
        fidelityData.multiple = Number(multiple);
        fidelityData.gift = gift;
      }

      await setDoc(doc(firestore, "fidelity", user.uid), fidelityData, { merge: true });
      await setDoc(doc(firestore, 'users', user.uid), { pseudo: userPseudo }, { merge: true });
      setUpdateStatus("Mise à jour effectuée avec succès !");
      setErrorStatus(null);
      setTimeout(() => setUpdateStatus(null), 3000);
    } catch (e) {
      console.error(e);
      setErrorStatus("Erreur lors de la mise à jour.");
      setUpdateStatus(null);
      setTimeout(() => setErrorStatus(null), 3000);
    }
  };

  const handleDownloadQRCode = () => {
    const finalWidth = 1748, finalHeight = 2480;
    const finalCanvas = document.createElement("canvas");
    finalCanvas.width = finalWidth;
    finalCanvas.height = finalHeight;
    const ctx = finalCanvas.getContext("2d");
    const bgImage = new Image();
    bgImage.crossOrigin = "anonymous";
    bgImage.src = QR_FRAME_SRC;

  bgImage.onload = () => {
      ctx.drawImage(bgImage, 0, 0, finalWidth, finalHeight);

      const qrCanvas = document.getElementById("qr-code-canvas");
      const qrFinalWidth = finalWidth * 0.55;
      const qrFinalHeight = qrFinalWidth;
      const qrX = (finalWidth - qrFinalWidth) / 2;
      const qrY = finalHeight * 0.55 - qrFinalHeight / 2;

      if (qrCanvas) {
        ctx.drawImage(qrCanvas, qrX, qrY, qrFinalWidth, qrFinalHeight);
      }

      const caption = (typeof shortId === 'string' && shortId)
        ? `ecobill.app/${shortId}`
        : '';

      if (caption) {
        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        ctx.fillStyle = "#111";
        const fontSize = Math.round(finalWidth * 0.028);
        ctx.font = `bold ${fontSize}px monospace`;
        const textX = finalWidth / 2;
        const textY = qrY + qrFinalHeight + Math.round(finalHeight * 0.015);
        ctx.fillText(caption, textX, textY);
        ctx.restore();
      }

      const dataUrl = finalCanvas.toDataURL("image/png", 1.0);
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = "QR-CODE ECOBILL.png";
      link.click();
    };

    bgImage.onerror = console.error;
  };

  const handleBackClick = () => navigate('/merchantgestion');

  // -------- Génération conforme à passwordPolicy.js --------
  const generateCompliantPassword = (minLength = 12) => {
    const lowers = 'abcdefghijklmnopqrstuvwxyz';
    const uppers = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = '0123456789';
    const specials = '!@#$%^&*()-_=+[]{};:,.?/\\';
    const all = lowers + uppers + digits + specials;

    const pick = (set) => set[Math.floor(Math.random() * set.length)];
    let pwd = [
      pick(lowers),
      pick(uppers),
      pick(digits),
      pick(specials),
    ];

    while (pwd.length < Math.max(minLength, 8)) {
      pwd.push(pick(all));
    }

    for (let i = pwd.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pwd[i], pwd[j]] = [pwd[j], pwd[i]];
    }
    const candidate = pwd.join('');
    if (PASSWORD_REGEX.test(candidate)) return candidate;

    for (let tries = 0; tries < 20; tries++) {
      const retry = generateCompliantPassword(minLength);
      if (PASSWORD_REGEX.test(retry)) return retry;
    }
    return 'Aa1!' + Math.random().toString(36).slice(2, 10);
  };

  // Ouvre la modale "Nouvelle caisse" → pré-génère un MDP conforme
  useEffect(() => {
    if (showNewCaisseModal && useAutoPassword) {
      setAutoPassword(generateCompliantPassword());
      setManualPassword('');
      setManualPwdTouched(false);
    }
  }, [showNewCaisseModal, useAutoPassword]); // eslint-disable-line

  const handleRegenerate = () => {
    setAutoPassword(generateCompliantPassword());
  };

  const isManualPwdValid = PASSWORD_REGEX.test(manualPassword || '');
  const isCreateCaisseDisabled =
    newCaisseLoading ||
    !newCaisseEmail.trim() ||
    (!useAutoPassword && !isManualPwdValid);

  // ───────────────── handleCreateNewCaisse ─────────────────
  const handleCreateNewCaisse = async (e) => {
    e.preventDefault();
    if (newCaisseLoading) return;

    setNewCaisseLoading(true);
    setNewCaisseMessage(null);
    setNewCaisseError(null);

    try {
      if (!newCaisseEmail) throw new Error("Veuillez entrer un email.");

      const companyUid = user?.uid;
      if (!companyUid) throw new Error("Utilisateur non authentifié.");

      // Rafraîchit le token pour s'assurer que les claims (role=commerce) sont à jour côté client
      try {
        const authUser = getAuth().currentUser;
        await authUser?.getIdToken(true);
        const tokenResult = await authUser?.getIdTokenResult?.();
        const roleClaim = tokenResult?.claims?.role;
        const isMerchantRole = ['commercant','commercantpending','cashier','affiliates','AffiliateRegie','hec','admin'].includes(roleClaim);
        if (!isMerchantRole) {
          throw new Error("Votre compte n'a pas le rôle marchand. Déconnectez-vous/reconnectez-vous ou contactez le support.");
        }
      } catch (err) {
        console.warn('[create-caisse] refresh token failed:', err);
        if (err?.message?.includes('rôle marchand')) {
          throw err;
        }
      }

      // 1) Validation MDP
      const password = useAutoPassword ? autoPassword : manualPassword;
      if (!PASSWORD_REGEX.test(password || '')) {
        throw new Error(PASSWORD_POLICY.message);
      }

      // 2) Canonicalisation robuste de l'email
      const rawEmail = String(newCaisseEmail).trim();
      const canonEmail = canonicalizeEmail(rawEmail);
      const emailLower = canonEmail.toLowerCase();

      // 3) Vérif locale : email déjà utilisé par une caisse de l'entreprise ?
      let existingCashiersSnap;
      try {
        existingCashiersSnap = await getDocs(
          query(
            collection(firestore, 'users'),
            where('companyuid', '==', companyUid),
            where('role', '==', 'cashier'),
            qLimit(50) // defensive: stay within reasonable bounds
          )
        );
      } catch (err) {
        console.error('[create-caisse] getDocs users/companyuid failed:', err);
        throw err;
      }

      const normalizeForCompare = (e) => {
        if (!e) {
          return { raw: '', lower: '', canon: '' };
        }
        const t = String(e).trim();
        return {
          raw: t,
          lower: t.toLowerCase(),
          canon: canonicalizeEmail(t).toLowerCase(),
        };
      };

      const target = normalizeForCompare(canonEmail);
      const alreadyInCompany = existingCashiersSnap.docs.some((d) => {
        const ed = d.data()?.email || '';
        const n = normalizeForCompare(ed);
        return n.lower === target.lower || n.canon === target.canon;
      });

      if (alreadyInCompany) {
        throw new Error("Une caisse avec cet email existe déjà dans votre entreprise.");
      }

      // 4) Pseudo "Caisse N" unique
      const existingPseudos = existingCashiersSnap.docs
        .map(d => String(d.data()?.pseudo || ''))
        .filter(Boolean);

      const nextNumber = (() => {
        const nums = existingPseudos
          .map(p => {
            const m = p.match(/^Caisse\s+(\d+)$/i);
            return m ? parseInt(m[1], 10) : null;
          })
          .filter(n => Number.isInteger(n));
        const max = nums.length ? Math.max(...nums) : 0;
        let candidate = max + 1;
        while (existingPseudos.includes(`Caisse ${candidate}`)) candidate += 1;
        return candidate;
      })();

      const pseudo = `Caisse ${nextNumber}`;

      // 5) Appel API de signup
      let res, data;
      try {
        res = await api.post('/api/auth/signup-cashier', {
          email: canonEmail,
          password,
          role: 'cashier',
          pseudo,
          companyuid: companyUid
        });
        data = res?.data ?? {};
      } catch (err) {
        const status = err?.response?.status;
        const msg = err?.response?.data?.error || err?.message || 'Erreur réseau.';
        if (status === 409 || String(msg).toLowerCase().includes('exists')) {
          setNewCaisseError("Cet email est déjà utilisé par un compte existant.");
        } else if (status === 403) {
          setNewCaisseError("Votre compte n'a pas les droits pour créer une caisse. Reconnectez-vous ou contactez le support.");
        } else {
          setNewCaisseError(msg);
        }
        setNewCaisseLoading(false);
        return;
      }

      // 6) UID créé
      const newCaisseId =
        data?.uid ??
        data?.userId ??
        data?.user?.uid ?? null;

      if (!newCaisseId) {
        console.error('Réponse signup inattendue:', data);
        throw new Error("Signup OK mais uid manquant dans la réponse API.");
      }

      // 7) Merge des champs locaux utiles
      const newUserDocRef = doc(firestore, 'users', newCaisseId);
      const existingUserDoc = await getDoc(newUserDocRef);

      if (existingUserDoc.exists()) {
        const prev = existingUserDoc.data() || {};
        const sameCompany = prev.companyuid === companyUid || prev.companyUid === companyUid;
        const isCashier = prev.role === 'cashier';
        if (!sameCompany || !isCashier) {
          throw new Error("Le compte existe déjà avec un autre rôle ou une autre entreprise. Contactez le support pour le lier.");
        }
        // Doc déjà conforme : on ne réécrit pas (évite l'erreur roleUnchanged des règles)
      } else {
        try {
          await setDoc(
            newUserDocRef,
            {
              role: 'cashier',            // nécessaire pour passer les règles Firestore (create)
              pseudo,
              companyuid: companyUid,
              companyUid: companyUid,      // variante camelCase utilisée ailleurs
              companyPseudo: user?.pseudo || null,
              email: canonEmail,
              emailLower,
              emailCanonical: emailLower
            }
          );
        } catch (err) {
          console.error('[create-caisse] setDoc users/newCaisse failed:', err);
          throw err;
        }
      }

      // 8) Associer le compte caisse au compte commerçant pour le switcher
      const linkResult = upsertLinkedAccountWithPassword({
        ownerUid: companyUid,
        uid: newCaisseId,
        email: canonEmail,
        password: password,
        pseudo: pseudo,
        role: 'cashier',
      });

      if (linkResult.ok) {
        try {
          await saveRemoteLinkedAccounts(companyUid, linkResult.accounts);
        } catch (err) {
          console.error('[MerchantFidGestion] sync linked accounts error:', err);
          // Non-blocking error
        }
      }

      // 9) Feedback UI
      setGeneratedPassword(password);
      setNewCaisseMessage(`Nouvelle caisse créée ! Mot de passe : ${password}`);
      setNewCaisseEmail('');
      if (useAutoPassword) setAutoPassword(generateCompliantPassword());
      setNewCaisseLoading(false);
      // Auto-close après délai pour laisser le temps de lire/noter le mot de passe
      setTimeout(() => {
        setShowNewCaisseModal(false);
        setNewCaisseMessage(null);
        setNewCaisseError(null);
      }, 3500);
    } catch (error) {
      console.error('Erreur création caisse :', error);
      setNewCaisseError(error?.message || "Erreur lors de la création de la nouvelle caisse.");
      setNewCaisseLoading(false);
    }
  };

  // Fermeture de la modale — confirmation si création en cours (évite une fermeture accidentelle)
  const handleCloseNewCaisseModal = () => {
    if (newCaisseLoading) {
      if (!window.confirm('La création de la caisse est en cours. Êtes-vous sûr de vouloir fermer ? Vous risquez de perdre le mot de passe généré.')) {
        return;
      }
    }
    setShowNewCaisseModal(false);
    setNewCaisseMessage(null);
    setNewCaisseError(null);
  };

  const handleResetPassword = async (email) => {
    const auth = getAuth();
    if (!email) return alert("Aucun email disponible pour cette caisse.");
    if (!window.confirm("Êtes-vous sûr de vouloir réinitialiser le mot de passe de cette caisse ?")) return;
    try {
      await sendPasswordResetEmail(auth, email);
      alert(`Email de réinitialisation envoyé à ${email}`);
    } catch (error) {
      console.error(error);
      alert("Impossible d'envoyer l'email de réinitialisation.");
    }
  };

  const handleDeleteCashier = async (caisse) => {
    const label = caisse.pseudo || caisse.email || caisse.id;
    if (!window.confirm(`Supprimer définitivement le compte "${label}" ?\n\nCette action est irréversible.`)) return;
    try {
      await api.post('/api/auth/delete-cashier', { cashierUid: caisse.id });
      // Nettoyer le linked-account du marchand (localStorage + Firestore /accountLinks)
      if (user?.uid) {
        deleteLinkedAccount(user.uid, caisse.id);
        removeRemoteLinkedAccountEntry(user.uid, caisse.id).catch(() => {});
      }
    } catch (err) {
      console.error('Erreur suppression caissier :', err);
      alert('Impossible de supprimer ce compte caissier.');
    }
  };

  const handleLevelChange = (index, field, value) => {
    setLevels(prevLevels => {
      const newLevels = prevLevels.map((l, i) => (i === index ? { ...l, [field]: value } : l));

      if (field === 'points') {
        let firstErrorMessage = null;
        const validatedLevels = newLevels.map((level, i, arr) => {
          let hasError = false;
          const currentPoints = Number(level.points);

          if (i > 0) {
            const prevPoints = Number(arr[i - 1].points);
            if (level.points && arr[i-1].points && currentPoints <= prevPoints) {
              hasError = true;
              if (!firstErrorMessage) {
                firstErrorMessage = `Les points du niveau ${i + 1} doivent être supérieurs à ceux du niveau ${i}.`;
              }
            }
          }
          
          return { ...level, error: hasError };
        });

        setErrorStatus(firstErrorMessage);

        return validatedLevels;
      }

      return newLevels;
    });
  };

  const addLevel = () => {
    setLevels([...levels, { points: "", gift: "", error: false }]);
  };

  const removeLevel = (index) => {
    if (levels.length > 1) {
      const newLevels = levels.filter((_, i) => i !== index);
      setLevels(newLevels);
    }
  };

  // ===== Rendu compact d'un niveau (mobile) : ligne encadrée + chip points =====
  const MobileLevelBox = ({ label, pointsId, pointsValue, onPointsChange, giftId, giftValue, onGiftChange, giftPlaceholder, onRemove, canRemove, isError }) => (
    <div className="level-box">
      <div className="level-top">
        <span className="level-label">{label}</span>
        <input
          type="number"
          id={pointsId}
          value={pointsValue}
          onChange={e => onPointsChange(e.target.value)}
          className="level-chip"
          inputMode="numeric"
          style={isError ? { borderColor: 'var(--eb-danger)', color: 'var(--eb-danger)', background: 'var(--eb-danger-bg)' } : {}}
        />
      </div>
      <input
        type="text"
        id={giftId}
        value={giftValue}
        onChange={e => onGiftChange(e.target.value)}
        className="input-ecb gift-input"
        placeholder={giftPlaceholder}
        style={{ width: '100%', marginTop: 8 }}
      />
      {canRemove && (
        <button onClick={onRemove} className="button-classic" style={{ marginTop: 8, width: '100%', minHeight: 40, color: 'var(--eb-danger)' }}>
          Supprimer
        </button>
      )}
    </div>
  );

  return (
    <div>
      <MerchantNavbar />
      <div className="dashboard-container">
        {/* Styles UI */}
        <style>{`
          .mfg-scope { width: 100%; min-width: 0; }
          .mfg-scope .eb-page-title { margin: 0; text-align: left; }
          .mfg-scope .btn-back { margin-bottom: 16px; }

          .mfg-scope .card { background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); overflow: hidden; box-shadow: var(--eb-shadow-sm); min-width: 0; }
          .mfg-scope .card-body { padding: 20px 24px 24px; }

          .mfg-scope .blue-header { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; padding: 18px 24px; background: var(--eb-surface); color: var(--eb-text); border-bottom: 1px solid var(--eb-border); }
          .mfg-scope .blue-header h3 { margin: 0; font-size: 16px; font-weight: 650; color: var(--eb-text); text-align: left; letter-spacing: -0.01em; }
          .mfg-scope .blue-header select { min-height: 38px; padding: 0 12px; font-size: 14px; font-weight: 600; }

          .mfg-scope .input-ecb { min-height: 42px; padding: 0 12px; font-size: 15px; min-width: 0; }

          .mfg-scope .grid-form { display: flex; flex-direction: column; gap: 14px; }
          .mfg-scope .form-row { display: flex; align-items: center; gap: 12px; width: 100%; }
          .mfg-scope .form-row > label:first-child { flex: 0 0 190px; font-size: 13.5px; font-weight: 600; color: var(--eb-text-2); }
          .mfg-scope .form-row .col-points { width: 96px; flex: 0 0 96px; text-align: center; }
          .mfg-scope .form-actions { display: flex; justify-content: center; gap: 10px; margin-top: 4px; }
          .mfg-scope .mfg-add { justify-content: flex-start; }
          .mfg-scope .mfg-submit { padding-top: 16px; margin-top: 8px; border-top: 1px solid var(--eb-border); }
          /* Cartes de la grille à hauteur égale : le bouton de validation se cale en bas de la carte */
          .mfg-scope .two-col-grid > .card { display: flex; flex-direction: column; }
          .mfg-scope .two-col-grid > .card > .card-body { flex: 1; display: flex; flex-direction: column; }
          .mfg-scope .two-col-grid > .card > .card-body > .grid-form { flex: 1; }
          .mfg-scope .two-col-grid .mfg-submit { margin-top: auto; }

          .mfg-scope .status-ok, .mfg-scope .status-err { margin-top: 4px; }

          .mfg-scope table.ecb { width: 100%; border-collapse: collapse; background: var(--eb-surface); font-size: 14px; }
          .mfg-scope table.ecb thead th { background: var(--eb-surface-2); color: var(--eb-muted); font-weight: 600; font-size: 12.5px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid var(--eb-border); padding: 10px 16px; text-align: left; }
          .mfg-scope table.ecb td { padding: 12px 16px; border-bottom: 1px solid var(--eb-border); vertical-align: middle; color: var(--eb-text-2); }
          .mfg-scope table.ecb tbody tr:last-child td { border-bottom: 0; }
          .mfg-scope table.ecb tbody tr:hover { background: var(--eb-surface-2); }
          .mfg-scope .mfg-table-wrap { border: 1px solid var(--eb-border); border-radius: var(--eb-radius); overflow: hidden; overflow-x: auto; }
          .mfg-scope table.ecb .col-pseudo { font-weight: 600; color: var(--eb-text); }

          .mfg-scope .email-cell { display: inline-block; max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }

          /* QR code (carte verticale) */
          .mfg-scope .mfg-qr-body { display: flex; flex-direction: column; align-items: center; gap: 16px; padding: 20px; text-align: center; }
          .mfg-scope .mfg-qr-help { margin: 0; font-size: 13px; line-height: 1.5; color: var(--eb-muted); max-width: 260px; }

          /* Grille responsive */
          .mfg-scope .two-col-grid { display: grid; grid-template-columns: 1fr; gap: 16px; align-items: stretch; }
          @media (min-width: 1024px) {
            .mfg-scope .two-col-grid { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
            /* 2 colonnes : le QR occupe sa ligne, en version compacte côte à côte */
            .mfg-scope .mfg-qr-card { grid-column: 1 / -1; }
            .mfg-scope .mfg-qr-body { flex-direction: row; justify-content: center; text-align: left; gap: 32px; }
            .mfg-scope .mfg-qr-body .eb-btn { width: auto; }
          }
          @media (min-width: 1280px) {
            /* 3 colonnes : QR | programme | satisfaction */
            .mfg-scope .two-col-grid { grid-template-columns: 300px minmax(0, 1fr) minmax(0, 1fr); }
            .mfg-scope .mfg-qr-card { grid-column: 1; grid-row: 1; }
            .mfg-scope .mfg-qr-body { flex-direction: column; text-align: center; gap: 16px; }
            .mfg-scope .mfg-qr-body .eb-btn { width: 100%; }
          }

          .mfg-scope .row-legend { font-size: 12.5px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: var(--eb-muted); }
          .mfg-scope .gift-input { min-width: 0; overflow-wrap: anywhere; white-space: normal; }

          .mfg-scope .sat-option { display: flex; align-items: flex-start; gap: 10px; padding: 12px 14px; border: 1px solid var(--eb-border); border-radius: var(--eb-radius); cursor: pointer; transition: border-color .15s ease, background-color .15s ease; }
          .mfg-scope .sat-option:hover { background: var(--eb-surface-2); }
          .mfg-scope .sat-option.is-checked { border-color: var(--eb-brand-300); background: var(--eb-brand-50); }
          .mfg-scope .sat-option input { margin-top: 3px; }

          /* Niveaux (mobile) */
          .mfg-scope .level-box { border: 1px solid var(--eb-border); border-radius: var(--eb-radius); padding: 12px; background: var(--eb-surface-2); }
          .mfg-scope .level-top { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
          .mfg-scope .level-label { font-weight: 600; font-size: 14px; color: var(--eb-text-2); }
          .mfg-scope .level-chip { width: 96px; min-height: 40px; text-align: center; padding: 0 10px; border-radius: var(--eb-radius-pill); font-weight: 600; }

          /* Modale nouvelle caisse (structure .eb-modal du design system) */
          .mfg-modal .eb-modal__title { text-align: left; }
          .mfg-modal form { padding: 16px 24px 24px; display: flex; flex-direction: column; gap: 14px; }
          .mfg-modal .input-ecb { width: 100%; min-height: 44px; padding: 0 12px; font-size: 15px; }
          .mfg-modal .mfg-seg { display: flex; flex-wrap: wrap; gap: 8px 16px; align-items: center; background: var(--eb-surface-2); border: 1px solid var(--eb-border); padding: 10px 14px; border-radius: var(--eb-radius-sm); font-size: 14px; }
          .mfg-modal .mfg-modal-actions { display: flex; justify-content: center; gap: 10px; margin-top: 4px; }

          @media (max-width: 768px) {
            .mfg-scope .card-body { padding: 16px; }
            .mfg-scope .blue-header { padding: 14px 16px; }
            .mfg-scope .form-row { flex-wrap: wrap; gap: 6px 10px; }
            .mfg-scope .form-row > label:first-child { flex: 1 1 100%; }
            .mfg-scope .form-row > .input-ecb { flex: 1 1 0 !important; min-width: 0 !important; }
            .mfg-scope .row-legend { display: none; }
            .mfg-scope .form-actions > button { flex: 1 1 auto; }
            .mfg-scope table.ecb thead th, .mfg-scope table.ecb td { padding: 10px 10px; font-size: 13px; }
            .mfg-scope table.ecb thead th { font-size: 11px; }
            .mfg-modal .eb-modal__head { padding: 18px 18px 0; }
            .mfg-modal form { padding: 14px 18px 18px; }
            .mfg-modal .mfg-modal-actions > button { width: 100%; }
          }
        `}</style>

        <div className="mfg-scope">
        <button onClick={handleBackClick} className="btn-back eb-btn eb-btn--ghost eb-btn--sm" style={{ paddingLeft: 8 }}>
          <FaArrowLeft /> Retour
        </button>
        <header className="eb-page-header">
          <div>
            <h1 className="eb-page-title">Paramètres de fidélité</h1>
            <p className="eb-page-subtitle">Programme, satisfaction et caisses de votre établissement</p>
          </div>
        </header>

        {/* ====== Grille: fidélité (gauche) | caisses (droite) ====== */}
        <div className="two-col-grid">
          {/* ─── CARD QR (colonne de gauche sur grand écran, en premier sur mobile) ─── */}
          {fidelityStatus !== "inactive" && (
            <div className="card card--square mfg-qr-card">
              <div className="blue-header rounded">
                <h3>Votre QR code</h3>
              </div>
              <div className="mfg-qr-body">
                <QRCodeDisplay
                  value={`https://ecobill.app/redirectqrc/${user?.uid || ""}`}
                  caption={shortId ? `ecobill.app/${shortId}` : ""}
                />
                <p className="mfg-qr-help">À imprimer et placer en caisse : vos clients le scannent pour cumuler leurs points.</p>
                <button
                  onClick={handleDownloadQRCode}
                  className="eb-btn eb-btn--primary eb-btn--block"
                  title="Télécharger le QR Code en haute qualité"
                >
                  <FaDownload /> Télécharger
                </button>
              </div>
            </div>
          )}

          {/* ─── CARD FIDÉLITÉ ─── */}
          <div className="card card--square">
            <div className="blue-header rounded">
              <h3>Programme de fidélité</h3>
              {fidelityStatus === "inactive" ? null : (
                <select
                  value={fidelityType}
                  onChange={e => setFidelityType(e.target.value)}
                  aria-label="Type de programme"
                  style={{ cursor: 'pointer' }}
                >
                  <option value="Par passage">Par passage</option>
                  <option value="Par points">Par points</option>
                </select>
              )}
            </div>

            <div className="card-body">
              {fidelityStatus === "inactive" ? (
                <div className="eb-alert eb-alert--info">
                  Votre demande est en cours de traitement
                </div>
              ) : (
                <div className="grid-form">
                  <div className="form-row">
                    <label htmlFor="userPseudo">{isPhone ? 'Nom :' : "Nom de l'entreprise :"}</label>
                    <input
                      type="text"
                      id="userPseudo"
                      value={userPseudo}
                      onChange={e => setUserPseudo(e.target.value)}
                      className="input-ecb"
                      style={{ flexGrow: 1, minWidth: 150 }}
                    />
                  </div>
                  {fidelityType === "Par passage" ? (
                    <>
                      <div className="form-row">
                        <label htmlFor="multiple">Nombres de passages :</label>
                        <input
                          type="number"
                          id="multiple"
                          value={multiple}
                          onChange={e => setMultiple(e.target.value)}
                          className="input-ecb"
                          style={{ flexGrow: 1, minWidth: 150 }}
                        />
                      </div>
                      <div className="form-row">
                        <label htmlFor="gift">Cadeau de la fidélité :</label>
                        <input
                          type="text"
                          id="gift"
                          value={gift}
                          onChange={e => setGift(e.target.value)}
                          className="input-ecb gift-input"
                          style={{ flexGrow: 1, minWidth: 150 }}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Desktop/tablette : présentation classique */}
                      {!isPhone && (
                        <>
                          <div className="form-row row-legend">
                            <label style={{ visibility: 'hidden' }}>—</label>
                            <span style={{ width: 96, flex: '0 0 96px', textAlign: 'center' }}>Points</span>
                            <span style={{ flexGrow: 1 }}>Gains</span>
                          </div>

                          {levels.map((level, index) => (
                            <div className="form-row" key={index}>
                              <label htmlFor={`points${index}`}>Niveau {index + 1} :</label>
                              <input
                                type="number"
                                id={`points${index}`}
                                value={level.points}
                                onChange={e => handleLevelChange(index, 'points', e.target.value)}
                                className="input-ecb col-points"
                                style={level.error ? { borderColor: 'var(--eb-danger)', background: 'var(--eb-danger-bg)' } : {}}
                              />
                              <input
                                type="text"
                                id={`gift${index}`}
                                value={level.gift}
                                onChange={e => handleLevelChange(index, 'gift', e.target.value)}
                                className="input-ecb gift-input"
                                style={{ flexGrow: 1, minWidth: 150 }}
                              />
                              {levels.length > 1 && (
                                <button onClick={() => removeLevel(index)} className="button-classic" aria-label="Supprimer ce niveau" style={{ color: 'var(--eb-danger)', minWidth: 42, minHeight: 42, fontSize: 18 }}>
                                  &times;
                                </button>
                              )}
                            </div>
                          ))}
                          <div className="form-actions mfg-add">
                            <button onClick={addLevel} className="eb-btn eb-btn--ghost eb-btn--sm">
                              + Ajouter un niveau
                            </button>
                          </div>
                        </>
                      )}

                      {/* Mobile : lignes encadrées + chip points */}
                      {isPhone && (
                        <>
                          {levels.map((level, index) => (
                            <MobileLevelBox
                              key={index}
                              label={`Niveau ${index + 1}`}
                              pointsId={`points${index}`}
                              pointsValue={level.points}
                              onPointsChange={value => handleLevelChange(index, 'points', value)}
                              giftId={`gift${index}`}
                              giftValue={level.gift}
                              onGiftChange={value => handleLevelChange(index, 'gift', value)}
                              giftPlaceholder="Ex: un café offert, -5%..."
                              onRemove={() => removeLevel(index)}
                              canRemove={levels.length > 1}
                              isError={level.error}
                            />
                          ))}
                          <div className="form-actions mfg-add">
                            <button onClick={addLevel} className="eb-btn eb-btn--secondary eb-btn--block">
                              + Ajouter un niveau
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  

                  {fidelityType === "Par passage" && (
                    <div className="form-row">
                      <label htmlFor="multiscan">Multi-scan :</label>
                      <label className="switch">
                        <input
                          type="checkbox"
                          id="multiscan"
                          checked={multiScan}
                          onChange={e => setMultiScan(e.target.checked)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  )}

                  <div className="form-actions mfg-submit">
                    <button
                      className="eb-btn eb-btn--primary"
                      onClick={handleUpdateFidelity}
                      disabled={isUpdateDisabled}
                      style={{ minWidth: 140 }}
                    >
                      Mettre à jour
                    </button>
                  </div>

                  {updateStatus && <div className="status-ok eb-alert eb-alert--success" role="status">{updateStatus}</div>}
                  {errorStatus && <div className="status-err eb-alert eb-alert--danger" role="alert">{errorStatus}</div>}
                </div>
              )}
            </div>
          </div>

          {/* ─── CARD SATISFACTION ─── */}
          <div className="card card--square">
            <div className="blue-header rounded">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaStar style={{ color: '#F5B301' }} /> Système de satisfaction
              </h3>
            </div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { value: 'ecobill',         label: 'Ecobill',           sub: 'notes & commentaires intégrés' },
                { value: 'google+facebook', label: 'Google / Facebook',  sub: 'rediriger vers vos avis externes' },
              ].map(({ value, label, sub }) => (
                <label key={value} className={`sat-option${satisfactionMode === value ? ' is-checked' : ''}`}>
                  <input
                    type="radio"
                    name="satMode"
                    value={value}
                    checked={satisfactionMode === value}
                    onChange={() => setSatisfactionMode(value)}
                  />
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontWeight: 600, color: 'var(--eb-text)', fontSize: 14.5 }}>{label}</span>
                    <span style={{ color: 'var(--eb-muted)', fontSize: 13 }}>{sub}</span>
                  </span>
                </label>
              ))}
              {satisfactionMode === 'google+facebook' && (
                <>
                  <input
                    type="url"
                    placeholder="URL fiche Google (optionnel)"
                    value={googleReviewUrl}
                    onChange={(e) => setGoogleReviewUrl(e.target.value.trim())}
                    className="eb-input"
                  />
                  <input
                    type="url"
                    placeholder="URL page Facebook (optionnel)"
                    value={facebookReviewUrl}
                    onChange={(e) => setFacebookReviewUrl(e.target.value.trim())}
                    className="eb-input"
                  />
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--eb-muted)' }}>
                    Remplissez une ou les deux URLs — seuls les boutons avec une URL seront affichés au client.
                  </p>
                </>
              )}
              <div className="form-actions mfg-submit">
                <button
                  className="eb-btn eb-btn--primary"
                  onClick={handleSaveSatisfactionMode}
                  disabled={savingSatisfaction}
                >
                  {satisfactionSaved
                    ? <><FaCheckCircle style={{ marginRight: 4 }} /> Enregistré</>
                    : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>

          {/* ─── CARD VOS CAISSES (span 2 colonnes, largeur des 2 carrés du haut) ─── */}
          <div className="card card--square" style={{ gridColumn: '1 / -1' }}>
            <div className="blue-header rounded">
              <h3>Vos caisses</h3>

              <button
                className="eb-btn eb-btn--primary eb-btn--sm"
                onClick={() => {
                  setShowNewCaisseModal(true);
                  setUseAutoPassword(true);
                  setAutoPassword(generateCompliantPassword());
                  setManualPassword('');
                  setManualPwdTouched(false);
                  setNewCaisseMessage(null);
                  setNewCaisseError(null);
                }}
              >
                + Nouvelle caisse
              </button>
            </div>

            <div className="card-body">
              {cashiers.length > 0 && (
                <div>
                  <div className="mfg-table-wrap">
                  <table className="ecb">
                    <thead>
                      <tr>
                        <th className="col-pseudo">Pseudo</th>
                        <th>Email</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...cashiers]
                        .sort((a, b) =>
                          (a.pseudo || '').localeCompare(b.pseudo || '', undefined, {
                            numeric: true,
                            sensitivity: 'base',
                          })
                        )
                        .slice(0, showAllCashiers ? cashiers.length : 3)
                        .map(caisse => {
                          const emailDisplay = isPhone
                            ? truncateForMobile(caisse.email, 15)
                            : caisse.email;
                          return (
                            <tr key={caisse.id}>
                              <td className="col-pseudo">{caisse.pseudo}</td>
                              <td>
                                <span className="email-cell" title={caisse.email}>
                                  {emailDisplay}
                                </span>
                              </td>
                              <td>
                                <div style={{ display:'inline-flex', gap: 8 }}>
                                  <button
                                    className="button-classic"
                                    onClick={() => handleResetPassword(caisse.email)}
                                    title="Réinitialiser le mot de passe"
                                    style={{ minWidth: isXS ? 38 : undefined, minHeight: 38 }}
                                  >
                                    {isXS ? <FaLock /> : 'Réinitialiser le mot de passe'}
                                  </button>
                                  <button
                                    className="button-delete"
                                    onClick={() => handleDeleteCashier(caisse)}
                                    title="Supprimer ce compte caissier"
                                    aria-label="Supprimer ce compte caissier"
                                    style={{ minWidth: 38, minHeight: 38 }}
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                  </div>

                  {/* Voir plus / Voir moins — centré */}
                  {cashiers.length > 3 && (
                    <div style={{ display:'flex', justifyContent:'center', marginTop: 16 }}>
                      <button
                        className="button-classic"
                        onClick={() => setShowAllCashiers(v => !v)}
                      >
                        {showAllCashiers ? 'Voir moins' : 'Voir plus'}
                      </button>
                    </div>
                  )}
                </div>
              )}

            {cashiers.length === 0 && (
              <p style={{ margin: 0, padding: '24px 16px', textAlign: 'center', color: 'var(--eb-muted)', fontSize: 14, border: '1px dashed var(--eb-border-strong)', borderRadius: 'var(--eb-radius)', background: 'var(--eb-surface-2)' }}>Aucune caisse pour le moment.</p>
            )}
          </div>
        </div>
        </div>

        {/* ===== Modale Nouvelle caisse ===== */}
        {showNewCaisseModal && (
          <div
            className="eb-modal-overlay mfg-modal"
            onClick={handleCloseNewCaisseModal}
            onKeyDown={(e) => {
              if (e.key === 'Escape') handleCloseNewCaisseModal();
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-caisse-title"
          >
            <div
              className="eb-modal modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="eb-modal__head">
                <h2 id="new-caisse-title" className="eb-modal__title">
                  Nouvelle caisse
                </h2>

                {/* Croix de fermeture */}
                <button
                  type="button"
                  aria-label="Fermer"
                  onClick={handleCloseNewCaisseModal}
                  className="eb-modal__close"
                >
                  ×
                </button>
              </div>

              {/* Body */}
              <form onSubmit={handleCreateNewCaisse}>
                {/* Email */}
                <div className="eb-field">
                  <label htmlFor="newCaisseEmail" className="eb-label">
                    Email
                  </label>
                  <input
                    id="newCaisseEmail"
                    type="email"
                    value={newCaisseEmail}
                    onChange={(e) => setNewCaisseEmail(e.target.value)}
                    required
                    placeholder="ex: caisse@votre-entreprise.com"
                    className="input-ecb"
                  />
                </div>

                {/* Choix du mode de mot de passe */}
                <div className="mfg-seg">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={useAutoPassword}
                      onChange={() => {
                        setUseAutoPassword(true);
                        setManualPwdTouched(false);
                      }}
                    />
                    <span>Automatique</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={!useAutoPassword}
                      onChange={() => {
                        setUseAutoPassword(false);
                        setManualPassword('');
                        setManualPwdTouched(false);
                      }}
                    />
                    <span>Définir manuellement</span>
                  </label>
                </div>

                {/* Zone mot de passe selon le mode */}
                {useAutoPassword ? (
                  <div className="eb-field">
                    <label className="eb-label">Mot de passe généré</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type="text"
                        readOnly
                        value={autoPassword}
                        className="input-ecb"
                        style={{ flex: 1, minWidth: 0, fontFamily: 'var(--eb-font-mono)' }}
                      />
                      <button
                        type="button"
                        className="eb-btn eb-btn--secondary"
                        onClick={handleRegenerate}
                        style={{ minHeight: 44 }}
                      >
                        Régénérer
                      </button>
                    </div>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--eb-muted)' }}>
                      Conforme à la politique de sécurité.
                    </p>
                  </div>
                ) : (
                  <div className="eb-field">
                    <label htmlFor="manualPwd" className="eb-label">
                      Mot de passe
                    </label>
                    <PasswordField
                      value={manualPassword}
                      onChange={(e) => setManualPassword(e.target.value)}
                      autoComplete="new-password"
                      aria-invalid={!!manualPassword && !isManualPwdValid}
                      className="input-ecb"
                      placeholder="8 caractères min., majuscule, chiffre, symbole"
                      inputStyle={{ width: '100%', border: manualPassword && !isManualPwdValid ? '1px solid var(--eb-danger)' : undefined }}
                    />
                  </div>
                )}

                {/* Messages */}
                {newCaisseMessage && (
                  <div className="eb-alert eb-alert--success" style={{ wordBreak: 'break-all' }}>
                    {newCaisseMessage}
                  </div>
                )}
                {newCaisseError && (
                  <div className="eb-alert eb-alert--danger" role="alert">
                    {newCaisseError}
                  </div>
                )}

                {/* Actions */}
                <div className="mfg-modal-actions">
                  <button
                    type="submit"
                    className="eb-btn eb-btn--primary"
                    disabled={isCreateCaisseDisabled}
                  >
                    {newCaisseLoading ? 'Création en cours…' : 'Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        </div>
      </div>
    </div>
  );
};

export default MerchantFidGestion;
